import {
  PAYABLE_STATUS_LABEL,
  PAYMENT_MODE_LABEL,
  STOCK_SHORTAGE_STATUS_LABEL,
  commercialReleaseDetails,
  commercialReleaseReasons,
  formatMoney,
  type PayableStatus,
  type SalesOrder,
} from "@cafegestor/shared";
import { useCallback, useState } from "react";
import { useCan } from "@/app/me";
import { KeyFacts, ProgressSteps } from "@/components/patterns";
import { Alert, Badge, Button, Card, Field, Input, Spinner, Table } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { Checkbox, SaveError } from "../cadastros/widgets";
import { dateBR, dateTimeBR, qty, useDocAction } from "../estoque/lib";
import { ReverseAction } from "../estoque/pickers";
import { MovementsTable } from "../estoque/SaldosPage";
import { EmissionPanel } from "../fiscal/EmissionPanel";
import { SalePaymentsEditor } from "../fiscal/SalePaymentsEditor";
import { ORDER_KEY, useOrder } from "./lib";
import { SalesStatusBadge } from "./pickers";
import { SaleOrderForm } from "./sale/SaleOrderForm";

const INVALIDATE = [ORDER_KEY, ["estoque"], ["financeiro"], ["vendas", "precos"]];

/** Área de ações do pedido (aprovar, confirmar): recebe o foco depois de salvar. */
const ACTIONS_ID = "pedido-acoes";
const money4 = (v: string | null | undefined) =>
  v === null || v === undefined ? "—" : `R$ ${formatQuantity4(v)}`;
const formatQuantity4 = (v: string) => {
  const [int = "0", frac = ""] = v.split(".");
  const f = frac.replace(/0+$/, "").padEnd(2, "0");
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${f}`;
};
const pct = (v: string | number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

/** Pedido existente (`id`) ou novo pedido (`id` null). */
export function OrderEditor({
  id,
  onClose,
  onCreated,
}: {
  id: string | null;
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const can = useCan();
  if (id === null) {
    return (
      <Card title="Novo pedido de venda">
        <SaleOrderForm
          order={null}
          canSellShort={can("vendas.venda_sem_estoque")}
          onClose={onClose}
          onCreated={(o) => onCreated?.(o.id)}
        />
      </Card>
    );
  }
  return <ExistingOrder id={id} onClose={onClose} />;
}

function ExistingOrder({ id, onClose }: { id: string; onClose: () => void }) {
  const order = useOrder(id);
  if (order.error) return <Alert tone="error">{errorMessage(order.error)}</Alert>;
  if (!order.data) return <Spinner />;
  return <OrderDetail key={order.data.id} o={order.data} onClose={onClose} />;
}

function OrderDetail({ o, onClose }: { o: SalesOrder; onClose: () => void }) {
  const can = useCan();
  const draft = o.status === "rascunho";
  const canEdit = draft && can("vendas.pedido.criar");
  // com alterações não salvas, a edição fica presa à versão em que começou (a API recusa o
  // salvar se o pedido mudou nesse meio-tempo) e o pedido não é aprovado
  const [pinned, setPinned] = useState<number | null>(null);
  const dirty = pinned !== null;
  const onDirtyChange = useCallback(
    (d: boolean) => setPinned((cur) => (d ? (cur ?? o.version) : null)),
    [o.version],
  );

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-5 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">
            Pedido {o.number} — {o.customerName}
          </h2>
          <SalesStatusBadge status={o.status} />
          {o.loadNumber && <Badge>Venda da rota — carga {o.loadNumber}</Badge>}
          {o.origin === "venda_direta" && <Badge>Venda Direta</Badge>}
        </div>
        {!canEdit && (
          <Button size="sm" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        )}
      </header>
      <div className="space-y-6 p-5 text-sm">
        <ProgressSteps
          label="Andamento do pedido"
          steps={["Rascunho", "Aprovado", "Confirmado"]}
          current={
            o.status === "rascunho"
              ? 0
              : o.status === "aprovado"
                ? 1
                : o.status === "confirmado"
                  ? 2
                  : -1
          }
          {...(o.status === "cancelado" || o.status === "estornado"
            ? { note: `Pedido ${o.status === "cancelado" ? "cancelado" : "estornado"}.` }
            : {})}
        />
        <KeyFacts
          items={[
            { label: "Data", value: dateBR(o.orderDate) },
            { label: "Vendedor", value: o.salespersonName ?? "—" },
            { label: "Depósito", value: o.warehouseName },
            { label: "Tabela de preço", value: o.priceListName ?? "sem tabela" },
            { label: "Pagamento", value: o.paymentMethodName ?? "—" },
            ...((o.paymentMode ?? o.expectedPaymentMode)
              ? [
                  {
                    label: "Recebimento",
                    value: PAYMENT_MODE_LABEL[(o.paymentMode ?? o.expectedPaymentMode)!],
                  },
                ]
              : []),
          ]}
        />
        {!canEdit && <Totals o={o} />}

        {(draft || o.status === "aprovado") && <Pending o={o} canEdit={canEdit} dirty={dirty} />}

        {canEdit ? (
          <SaleOrderForm
            key={pinned ?? o.version}
            order={o}
            baseVersion={pinned ?? o.version}
            canSellShort={can("vendas.venda_sem_estoque")}
            onClose={onClose}
            onCreated={() => undefined}
            onDirtyChange={onDirtyChange}
          />
        ) : (
          <>
            <InstallmentsView o={o} />
            <div>
              <h3 className="mb-2 font-semibold">Itens</h3>
              <ItemsTable o={o} />
            </div>
          </>
        )}

        {(o.availability?.length ?? 0) > 0 && (draft || o.status === "aprovado") && (
          <AvailabilityView o={o} />
        )}

        {/* chave pela versão: liberações marcadas não sobrevivem a uma mudança do pedido */}
        <div id={ACTIONS_ID} tabIndex={-1} className="outline-none">
          <Actions key={o.version} o={o} dirty={dirty} />
        </div>

        <Outcome o={o} />

        {o.status === "confirmado" && (
          <>
            <SalePaymentsEditor order={o} />
            <EmissionPanel
              use={o.loadId ? "venda_rota" : "venda_fabrica"}
              sourceId={o.id}
              title="Nota fiscal da venda"
            />
          </>
        )}

        {(o.movements?.length ?? 0) > 0 && (
          <div>
            <h3 className="mb-2 font-semibold">Movimentos de estoque</h3>
            <MovementsTable rows={o.movements ?? []} />
          </div>
        )}

        {(o.shortages?.length ?? 0) > 0 && <ShortagesView o={o} />}
      </div>
    </section>
  );
}

/** v7: o que foi vendido sem estoque (baixa pendente, a regularizar em Estoque → Pendências). */
function ShortagesView({ o }: { o: SalesOrder }) {
  return (
    <div>
      <h3 className="mb-2 font-semibold">Vendido sem estoque</h3>
      <p className="mb-2 text-muted-foreground">
        A quantidade abaixo saiu sem saldo no depósito
        {o.stockShortageByEmail ? `, autorizada pelo perfil de ${o.stockShortageByEmail}` : ""}. A
        baixa é feita na regularização (Estoque → Pendências de estoque), no lote que atender a
        venda; até lá, o custo desta venda é provisório.
      </p>
      <Table head={["Produto", "Sem estoque", "Regularizado", "Situação"]}>
        {o.shortages?.map((s) => (
          <tr key={s.itemId}>
            <td className="px-3 py-2 font-mono text-xs">{s.productCode}</td>
            <td className="px-3 py-2 text-right">{qty(s.quantity)}</td>
            <td className="px-3 py-2 text-right">{qty(s.regularized)}</td>
            <td className="px-3 py-2">
              <Badge tone={s.status === "aberta" ? "warn" : "neutral"}>
                {STOCK_SHORTAGE_STATUS_LABEL[s.status]}
              </Badge>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Totals({ o }: { o: SalesOrder }) {
  const rows: [string, string][] = [
    ["Produtos", o.productsTotal],
    ["Descontos", o.discountTotal],
    ["Frete", o.freightTotal],
    ["Outras", o.otherTotal],
  ];
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-md bg-muted px-4 py-3">
      {rows
        .filter(([, v]) => Number(v) !== 0)
        .map(([l, v]) => (
          <span key={l}>
            {l}: {formatMoney(v)}
          </span>
        ))}
      <span className="font-semibold">Total do pedido: {formatMoney(o.totalAmount)}</span>
      {o.commissionPercent !== null && <span>Comissão: {pct(o.commissionPercent)}</span>}
      {o.credit && (
        <span className={o.credit.exceeded ? "font-semibold text-destructive" : ""}>
          Crédito: limite {o.credit.limit === null ? "sem limite" : formatMoney(o.credit.limit)}
          {" · "}em aberto {formatMoney(o.credit.exposure)}
        </span>
      )}
    </div>
  );
}

function Pending({ o, canEdit, dirty }: { o: SalesOrder; canEdit: boolean; dirty: boolean }) {
  const issues = o.issues ?? [];
  const warnings = o.warnings ?? [];
  // o texto vem da API (PRICES_OUTDATED): a tabela válida hoje mudou
  const outdated = issues.some((i) => i.includes("Atualizar preços"));
  const reprice = useDocAction<{ version: number }, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/atualizar-precos`,
    invalidate: INVALIDATE,
  });
  return (
    <>
      {issues.length > 0 && (
        <Alert tone="warn">
          <p className="font-medium">
            Para {o.status === "rascunho" ? "aprovar" : "confirmar"}, falta:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
          {outdated && canEdit && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={dirty || reprice.isPending}
                onClick={() => reprice.mutate({ version: o.version })}
              >
                Atualizar preços
              </Button>
              <SaveError error={reprice.error} />
            </div>
          )}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert tone="info">
          <ul className="list-disc pl-5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
}

function InstallmentsView({ o }: { o: SalesOrder }) {
  if ((o.installments?.length ?? 0) === 0) return null;
  return (
    <div>
      <h3 className="mb-2 font-semibold">Parcelas a receber</h3>
      <Table head={["Parcela", "Vencimento", "Valor"]}>
        {o.installments?.map((p) => (
          <tr key={p.id}>
            <td className="px-3 py-2">{p.number}</td>
            <td className="px-3 py-2">{dateBR(p.dueDate)}</td>
            <td className="px-3 py-2 text-right">{formatMoney(p.amount)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function ItemsTable({ o }: { o: SalesOrder }) {
  if ((o.items?.length ?? 0) === 0) return <p className="text-muted-foreground">Nenhum item.</p>;
  return (
    <Table head={["#", "Produto", "Quantidade", "Tabela", "Preço", "Desconto", "Desc. %", "Total"]}>
      {o.items?.map((i) => (
        <tr key={i.id}>
          <td className="px-3 py-2">{i.lineNumber}</td>
          <td className="px-3 py-2">
            <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
          </td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{qty(i.quantity, i.unitCode)}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            {i.listPrice === null ? <Badge tone="warn">fora da tabela</Badge> : money4(i.listPrice)}
          </td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{money4(i.unitPrice)}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{formatMoney(i.discount)}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            {pct(i.discountPercent)}
            {i.overLimit && (
              <>
                {" "}
                <Badge tone="warn">acima do máx. {pct(i.maxDiscountPercent)}</Badge>
              </>
            )}
          </td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{formatMoney(i.total)}</td>
        </tr>
      ))}
    </Table>
  );
}

function AvailabilityView({ o }: { o: SalesOrder }) {
  const reserved = o.status === "aprovado";
  return (
    <div>
      <h3 className="mb-2 font-semibold">
        Estoque no depósito {reserved ? "(este pedido já está reservado)" : ""}
      </h3>
      <Table
        head={[
          "Produto",
          "Necessário",
          "Saldo vendável",
          "Reservado",
          "A regularizar",
          "Disponível",
          "",
        ]}
      >
        {o.availability?.map((a) => (
          <tr key={a.productId}>
            <td className="px-3 py-2 font-mono text-xs">{a.productCode}</td>
            <td className="px-3 py-2 text-right">{qty(a.needed)}</td>
            <td className="px-3 py-2 text-right">{qty(a.sellable)}</td>
            <td className="px-3 py-2 text-right">{qty(a.reserved)}</td>
            <td className="px-3 py-2 text-right">{qty(a.pending)}</td>
            <td className="px-3 py-2 text-right">{qty(a.available)}</td>
            <td className="px-3 py-2">
              {a.enough ? <Badge tone="ok">ok</Badge> : <Badge tone="error">insuficiente</Badge>}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------- ações
function Actions({ o, dirty }: { o: SalesOrder; dirty: boolean }) {
  const can = useCan();
  const [releaseDiscount, setReleaseDiscount] = useState(false);
  const [releaseCredit, setReleaseCredit] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const approve = useDocAction<unknown, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/aprovar`,
    invalidate: INVALIDATE,
    successMessage: (r) => `Pedido ${r.number} aprovado: estoque reservado.`,
  });
  const confirm = useDocAction<unknown, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/confirmar`,
    invalidate: INVALIDATE,
    successMessage: (r) =>
      `Venda ${r.number} confirmada: estoque baixado e ${
        r.paymentMode === "a_vista" ? "recebimento registrado" : "título a receber criado"
      }.`,
  });
  const reopen = useDocAction<{ version: number }, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/reabrir`,
    invalidate: INVALIDATE,
  });
  const cancel = useDocAction<{ version: number; reason: string }, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/cancelar`,
    invalidate: INVALIDATE,
  });
  const reverse = useDocAction<{ reason: string }, SalesOrder>({
    path: () => `/vendas/pedidos/${o.id}/estornar`,
    invalidate: INVALIDATE,
  });

  if (o.status === "confirmado") {
    return can("vendas.pedido.cancelar") ? (
      <ReverseAction
        label="Estornar venda"
        pending={reverse.isPending}
        error={reverse.error}
        onConfirm={(reason) => reverse.mutate({ reason })}
      />
    ) : null;
  }
  if (o.status !== "rascunho" && o.status !== "aprovado") return null;

  const draft = o.status === "rascunho";
  const issues = o.issues ?? [];
  const canApprove = draft && can("vendas.pedido.criar");
  const canConfirm = can("vendas.pedido.confirmar");
  const canCancel = can("vendas.pedido.cancelar");
  const canReopen = !draft && can("vendas.pedido.criar");
  if (!canApprove && !canConfirm && !canCancel && !canReopen) return null;

  // liberações: quem não tem a permissão não vê a opção (a API recusa de qualquer modo)
  const needDiscount = draft && (o.needsDiscountRelease ?? false);
  // a liberação é uma só (mesma regra e permissão); o texto diz o caso de cada item
  const releaseItems = o.items ?? [];
  const reasons = commercialReleaseReasons(releaseItems);
  const releaseText = [
    reasons.includes("preco_fora_da_tabela") ? "preço fora da tabela" : null,
    reasons.includes("desconto_acima_do_maximo") ? "desconto acima do máximo da tabela" : null,
  ]
    .filter(Boolean)
    .join(" e ");
  const releaseDetails = commercialReleaseDetails(releaseItems).join("; ");
  const needCredit = draft && (o.needsCreditRelease ?? false);
  const mayDiscount = can("vendas.desconto.acima_limite");
  const mayCredit = can("vendas.credito.liberar");
  const blockedRelease = (needDiscount && !mayDiscount) || (needCredit && !mayCredit);
  const missingRelease =
    (needDiscount && mayDiscount && !releaseDiscount) ||
    (needCredit && mayCredit && !releaseCredit);
  const body = { version: o.version, releaseDiscount, releaseCredit };
  const busy = approve.isPending || confirm.isPending || reopen.isPending || cancel.isPending;
  const stockShort = draft && (o.stockShort ?? false);
  const disabledMain =
    dirty || busy || issues.length > 0 || blockedRelease || missingRelease || stockShort;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <h3 className="font-semibold">{draft ? "Aprovar ou confirmar" : "Confirmar venda"}</h3>
      <p className="text-muted-foreground">
        {draft
          ? "Aprovar reserva o estoque (por produto e depósito; o lote só é escolhido na confirmação). "
          : "O estoque deste pedido está reservado. "}
        Confirmar escolhe os lotes (o que vence primeiro, nunca vencido), baixa o estoque e{" "}
        {(o.paymentMode ?? o.expectedPaymentMode) === "a_vista"
          ? "registra o recebimento à vista no pedido"
          : "cria o título em Contas a receber com as parcelas"}
        . Confirmada, a venda só pode ser estornada.
      </p>
      {dirty && <Alert tone="warn">Salve as alterações antes de aprovar ou confirmar.</Alert>}
      {stockShort && (
        <Alert tone="warn">
          Falta estoque disponível para venda: veja a tabela de estoque acima.
        </Alert>
      )}
      {needDiscount &&
        (mayDiscount ? (
          <div>
            <Checkbox
              label={`Liberar ${releaseText || "preço/desconto"} (fica registrado na auditoria)`}
              checked={releaseDiscount}
              onChange={(ev) => setReleaseDiscount(ev.target.checked)}
            />
            {releaseDetails && (
              <p className="ml-6 text-xs text-muted-foreground">Itens: {releaseDetails}.</p>
            )}
          </div>
        ) : (
          <Alert tone="warn">
            {releaseDetails
              ? `Exige liberação comercial: ${releaseDetails}.`
              : "Exige liberação comercial de preço ou desconto."}{" "}
            Peça a um usuário autorizado.
          </Alert>
        ))}
      {needCredit &&
        (mayCredit ? (
          <Checkbox
            label="Liberar venda acima do limite de crédito do cliente (fica registrado na auditoria)"
            checked={releaseCredit}
            onChange={(ev) => setReleaseCredit(ev.target.checked)}
          />
        ) : (
          <Alert tone="warn">
            Venda acima do limite de crédito do cliente: peça a liberação a um usuário autorizado.
          </Alert>
        ))}
      <div className="flex flex-wrap gap-2">
        {canApprove && (
          <Button variant="secondary" disabled={disabledMain} onClick={() => approve.mutate(body)}>
            {approve.isPending ? "Aprovando..." : "Aprovar (reservar estoque)"}
          </Button>
        )}
        {/* rascunho: confirmar também aprova, então exige as duas permissões */}
        {canConfirm && (!draft || canApprove) && (
          <Button disabled={disabledMain} onClick={() => confirm.mutate(body)}>
            {confirm.isPending ? "Confirmando..." : "Confirmar venda"}
          </Button>
        )}
        {canReopen && (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => reopen.mutate({ version: o.version })}
          >
            Reabrir para edição (libera a reserva)
          </Button>
        )}
      </div>
      {canCancel && (
        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <Field label="Motivo do cancelamento">
            <Input
              className="w-72"
              value={cancelReason}
              onChange={(ev) => setCancelReason(ev.target.value)}
            />
          </Field>
          <Button
            variant="danger"
            disabled={busy || dirty || cancelReason.trim().length < 3}
            onClick={() => cancel.mutate({ version: o.version, reason: cancelReason })}
          >
            Cancelar pedido
          </Button>
        </div>
      )}
      <SaveError error={approve.error ?? confirm.error ?? reopen.error ?? cancel.error} />
    </div>
  );
}

/** Resultado financeiro e trilha: título, recebimento à vista, liberações, cancelamento/estorno. */
function Outcome({ o }: { o: SalesOrder }) {
  const can = useCan();
  return (
    <div className="space-y-2">
      {o.discountOverrideAt && (
        <p className="text-muted-foreground">
          Liberação comercial por {o.discountOverrideByEmail ?? "—"} em{" "}
          {dateTimeBR(o.discountOverrideAt)}
          {commercialReleaseDetails(o.items ?? []).length > 0
            ? `: ${commercialReleaseDetails(o.items ?? []).join("; ")}`
            : ""}
          .
        </p>
      )}
      {o.creditReleasedAt && (
        <p className="text-muted-foreground">
          Crédito liberado por {o.creditReleasedByEmail ?? "—"} em {dateTimeBR(o.creditReleasedAt)}{" "}
          (limite {o.creditLimitSnapshot === null ? "—" : formatMoney(o.creditLimitSnapshot)}, em
          aberto com o pedido{" "}
          {o.creditExposureSnapshot === null ? "—" : formatMoney(o.creditExposureSnapshot)}).
        </p>
      )}
      {o.receivableNumber && (
        <Alert tone={o.receivableStatus === "cancelado" ? "warn" : "ok"}>
          Título a receber <span className="font-mono">{o.receivableNumber}</span> —{" "}
          {PAYABLE_STATUS_LABEL[(o.receivableStatus ?? "aberto") as PayableStatus]}.{" "}
          {can("financeiro.ver") && o.receivableId && (
            <a
              href={`/financeiro/contas-a-receber?titulo=${encodeURIComponent(o.receivableId)}`}
              className="underline"
            >
              Ver em Contas a receber
            </a>
          )}
        </Alert>
      )}
      {o.receipt && (
        <Alert tone={o.receipt.status === "estornado" ? "warn" : "ok"}>
          {o.receipt.status === "estornado" ? "Recebimento à vista estornado" : "Recebido à vista"}:{" "}
          {formatMoney(o.receipt.amount)} em {o.receipt.paymentMethodName} (
          {dateTimeBR(o.receipt.receivedAt)}
          {o.receipt.receivedByEmail ? `, por ${o.receipt.receivedByEmail}` : ""}).
          {o.receipt.reversedAt ? ` Estornado em ${dateTimeBR(o.receipt.reversedAt)}.` : ""}
        </Alert>
      )}
      {o.status === "cancelado" && (
        <Alert tone="warn">
          Cancelado em {dateTimeBR(o.cancelledAt)}
          {o.cancelReason ? `. Motivo: ${o.cancelReason}` : ""}.
        </Alert>
      )}
      {o.status === "estornado" && (
        <Alert tone="warn">
          Estornado em {dateTimeBR(o.reversedAt)}. Motivo: {o.reversalReason}
        </Alert>
      )}
    </div>
  );
}
